import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { CHAVE_DONO, chatCarregado, donoGuardado, ID_SCRIPT, limparChatManifest } from '@/lib/chatManifest';
import { LogoSlack } from '@/componentes/entradas/MeuSlack';

import { acaoTexto } from '@/componentes/ui/botao';
/**
 * O chatbot da Manifest (sem bolha: abre pelo botão do cabeçalho). O widget é da
 * Manifest; aqui só se declara `window.ManifestChat` e carrega o `loader.js`
 * UMA vez — e, para quem ainda não ligou o Slack, mostra o convite de
 * "Conectar com o Slack" logo acima dela. Tudo com a
 * configuração que a API devolve para quem está logado — a chave por usuário
 * (EndUser) é cunhada no servidor; a chave de admin da Manifest nunca chega
 * aqui. Ver apps/api/src/modules/chatbot/rotas.ts.
 */

interface ConfigChatbot {
  ativo: boolean;
  team?: string;
  task?: string;
  mode?: 'chat' | 'canvas';
  script?: string;
  userToken?: string | null;
  motivo?: string | null;
  /** O Slack não está ligado ao login: o chat abre anônimo. */
  conectarSlack?: boolean;
}

interface ManifestChatGlobal {
  team: string;
  task: string;
  mode: string;
  /** Sem a bolha flutuante: abre pelo botão do cabeçalho (toggle). */
  hidden?: boolean;
  toggle?: (mensagem?: string) => void;
  close?: () => void;
  userToken?: string;
  onAuthError?: () => void;
  setUserToken?: (token: string) => void;
}

declare global {
  interface Window {
    ManifestChat?: ManifestChatGlobal;
  }
}

export function ChatbotManifest(): JSX.Element | null {
  const config = useQuery<ConfigChatbot>({
    queryKey: ['chatbot'],
    queryFn: () => api.get('/api/chatbot'),
    staleTime: Infinity,
    retry: false,
  });

  const { usuario } = useSessao();
  const eu = usuario ? String(usuario.usuario) : null;

  React.useEffect(() => {
    const c = config.data;
    if (!c || !eu) return;
    // Sessão guardada de OUTRO usuário: apaga. Se o widget já está na página
    // (troca de login sem recarregar), só um reload tira a sessão do iframe.
    if (donoGuardado() !== eu) {
      limparChatManifest();
      if (chatCarregado()) {
        window.location.reload();
        return;
      }
    }
    // Sem chave da pessoa (Slack não ligado, ou Manifest fora), o widget NÃO
    // carrega: anônimo, ele herdaria a sessão guardada ou responderia com erro.
    // Fica só o convite de "Conectar com o Slack".
    if (!c.ativo || !c.team || !c.task || !c.script || !c.userToken || chatCarregado()) {
      if (c.motivo) console.info(`Chatbot: ${c.motivo}`);
      return;
    }
    try {
      localStorage.setItem(CHAVE_DONO, eu);
    } catch {
      /* sem storage, a Manifest também não guarda sessão */
    }
    window.ManifestChat = {
      team: c.team,
      task: c.task,
      mode: c.mode ?? 'chat',
      // A bolha da Manifest cobria botões no canto das telas (Emitir, + Destino,
      // paginação). Pedido do Paulo, 28/10/2026: abre pelo botão do cabeçalho.
      hidden: true,
      userToken: c.userToken,
      onAuthError: () => {
        // Chave vencida ou recusada: pede outra ao WMS e entrega ao widget.
        void api.get<ConfigChatbot>('/api/chatbot?renovar=1').then((n) => {
          if (n.userToken) window.ManifestChat?.setUserToken?.(n.userToken);
        });
      },
    };
    const s = document.createElement('script');
    s.id = ID_SCRIPT;
    s.src = c.script;
    s.async = true;
    document.body.appendChild(s);
  }, [config.data, eu]);

  return null;
}

/**
 * O assistente no cabeçalho (ao lado do sino): abre e fecha a janela do chat
 * — Ctrl+J também. Quem ainda não ligou o Slack vê, no lugar, o convite para
 * conectar (antes era um cartão fixo no canto da tela, em toda página).
 */
export function BotaoAssistente(): JSX.Element | null {
  const config = useQuery<ConfigChatbot>({ queryKey: ['chatbot'], queryFn: () => api.get('/api/chatbot'), staleTime: Infinity, retry: false });
  const [convite, definirConvite] = React.useState(false);
  const c = config.data;
  const pronto = Boolean(c?.ativo && c.userToken);
  const alternar = React.useCallback((): void => {
    if (pronto) window.ManifestChat?.toggle?.();
    else definirConvite((x) => !x);
  }, [pronto]);

  React.useEffect(() => {
    const tecla = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        alternar();
      }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [alternar]);

  if (!c || (!c.ativo && !c.conectarSlack)) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={alternar}
        aria-label="Assistente (Ctrl+J)"
        title="Assistente (Ctrl+J)"
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
      >
        <Sparkles className="h-[18px] w-[18px]" />
        {c.conectarSlack ? <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-status-atencao" aria-hidden /> : null}
      </button>
      {convite && c.conectarSlack ? (
        <>
          <button type="button" className="fixed inset-0 z-30 cursor-default" aria-label="Fechar" onClick={() => definirConvite(false)} />
          <div className="absolute right-0 top-12 z-40 flex w-[260px] items-start gap-2 rounded-xl border border-borda bg-superficie p-3 text-xs shadow-lg">
            <LogoSlack className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1.5">
              <p className="text-textoFraco">Conecte seu Slack para o assistente reconhecer você e poder consultar por você.</p>
              <a
                href={`/api/slack/conectar?voltar=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className={acaoTexto}
              >
                Conectar com o Slack
              </a>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
